import { IAppliedFilter, Guid } from "@/types"
import { Purpose, ArticleSearchScope, FilterCategory } from "config/constants"
import { AssetOwnershipType, AssetStatus, AssetAvailability } from "models/Asset"
import { ValuationStatus } from "models/Valuation"
import { InvoiceAssignmentStatus } from "models/Invoice"
import { ReservationStatus } from "models/Reservation"
import { ShippingOrderDisplayStatus, ShippingOrderType } from "models/ShippingOrder"
import { AssetStoredStatus } from 'models/AssetDisplayStatus'
import { OutgoingInvoiceStatus, OutgoingInvoiceType } from "models/OutgoingInvoice"
import { NotificationChannel, NotificationType } from "models/Notification"
import { OfferStatus } from "models/Offer"
import { ArticleClass } from "models/ArticleClassInfo"
import { ShippingMode } from "models/ShippingOrderOverviewItem"
import { LabelStatus } from "models/LabelGroup"
import { Role } from "models/UserRoles"
import { AssetMaintenanceStatus } from "models/AssetMaintenance"

export function collectFilterProps(filters: IAppliedFilter[]) {

    if (filters.length === 0) return {}

    const articleIds: Guid[] = []
    let assetIds: Guid[] = []
    const purposes: Purpose[] = []
    const lineIds: Guid[] = []
    const familyIds: Guid[] = []
    const modelIds: Guid[] = []
    const yearOfConstructions: number[] = []
    const emissionLevels: string[] = []
    const reservationNumbers: string[] = []
    const inventoryNumbers: string[] = []
    const serialNumbers: string[] = []
    const serialNumbersMachine: string[] = []
    const manufacturerCodes: string[] = []
    const financeAssetLmids: string[] = []
    const financeSupplierOrderNumber: string[] = []
    const financeInternalOrderNumber: string[] = []
    const internalOrderNumbers: string[] = []
    const supplierOrderNumbers: string[] = []
    const invoiceNumbers: string[] = []
    const referenceNumbers: string[] = []
    const referenceTexts: string[] = []
    const assignmentStatuses: InvoiceAssignmentStatus[] = []
    const valuationStatuses: ValuationStatus[] = []
    const customerNames: string[] = []
    const customerNumbers: string[] = []
    const crmReferenceNumbers: string[] = []
    const drawingNumbers: string[] = []
    const labeled: boolean[] = []
    const labels: string[] = []
    const operational: boolean[] = []
    const connectedAssets: boolean[] = []
    const shortages: boolean[] = []
    const missingDepreciationRates: boolean[] = []
    const assetStatus: AssetStatus[] = []
    const availabilities: AssetAvailability[] = []
    const reservationStatuses: ReservationStatus[] = []
    const salesAdminNumbers: string[] = []
    const salesPersonNumbers: string[] = []
    const homebaseIds: (Guid | null)[] = []
    const warehouseIds: (Guid | null)[] = []
    const responsibleWarehouseIds: (Guid | null)[] = []
    const profitCenterIds: Guid[] = []
    const colorIds: (Guid | null)[] = []
    const shippingOrderTypes: ShippingOrderType[] = []
    const shippingOrderStates: ShippingOrderDisplayStatus[] = []
    const shippingOrderNumbers: string[] = []
    const outgoingInvoiceTypes: OutgoingInvoiceType[] = []
    const outgoingInvoiceStatuses: OutgoingInvoiceStatus[] = []
    const erpOrderNumbers: string[] = []
    const erpInvoiceNumbers: string[] = []
    const notificationChannels: NotificationChannel[] = []
    const notificationTypes: NotificationType[] = []
    const offerStatuses: OfferStatus[] = []
    const offerNumbers: string[] = []
    const automaticInvoicing: boolean[] = []
    const articleClasses: ArticleClass[] = []
    const quickCouplingSystems: string[] = []
    const connectionTypes: string[] = []
    const locking: string[] = []
    const controlSystems: string[] = []
    const agility: string[] = []
    const referenceNumbersSale: string[] = []
    const referenceNumbersRental: string[] = []
    let ownershipType: AssetOwnershipType | undefined
    const costCategoryIds: string[] = []
    const completeModes: ShippingMode[] = []
    const articleNumbers: string[] = []
    const categoryIds: (Guid | null)[] = []
    const labelStatus: LabelStatus[] = []
    const roles: Role[] = []
    const assetCategories: Guid[] = []
    const divisions: Guid[] = []
    const tenantIds: Guid[] = []
    const maintenanceTypeIds: Guid[] = []
    const vatCodes: string[] = []
    const statuses: (AssetMaintenanceStatus | AssetStatus)[] = []
    const assetTypes: string[] = []

    let invoiceDate: string | undefined
    let disposalDateDiffersCreationPeriod: boolean | undefined = undefined
    let disposalDateDiffersInvoicePeriod: boolean | undefined = undefined
    let invoiceDateDiffersCreationPeriod: boolean | undefined = undefined
    let invoicePendingMoreThanDays: number | undefined = undefined
    let maxDistance: number | undefined = undefined
    let criteria: string | undefined
    let customerNameCriteria: string | undefined
    let scope: ArticleSearchScope[] | undefined
    let stateOfStorage: AssetStoredStatus | undefined
    let startsWithinDays: number | undefined = undefined
    let endsWithinDays: number | undefined = undefined
    let condition: [number | null, number | null] | undefined = undefined
    let initialCondition: boolean | undefined = undefined
    let cuttingEdge: boolean | undefined = undefined
    let teeth: boolean | undefined = undefined
    let heavyDuty: boolean | undefined = undefined
    let availabilityDate: [string | null, string | null] | undefined = undefined
    let lastScanDate: [string | null, string | null] | undefined = undefined
    let length: [number | null, number | null] | undefined = undefined
    let width: [number | null, number | null] | undefined = undefined
    let height: [number | null, number | null] | undefined = undefined
    let volume: [number | null, number | null] | undefined = undefined
    let lastInvoice: boolean | undefined = undefined
    let invoiceBeforeShipping: boolean | undefined = undefined
    let invoiceBlocked: boolean | undefined = undefined
    let rentToBuy: number | undefined = undefined

    for (const filter of filters) {
        switch (filter.category) {
            case FilterCategory.all:
                criteria = filter.value
                break
            case FilterCategory.customerNameSearch:
                customerNameCriteria = filter.value
                break
            case FilterCategory.article:
                articleIds.push(filter.value)
                break
            case FilterCategory.asset:
            case FilterCategory.lmid:
                assetIds.push(filter.value)
                break
            case FilterCategory.reservationNumber:
                reservationNumbers.push(filter.value)
                break
            case FilterCategory.reservationNumberAssetIds:
                assetIds = [...assetIds, ...filter.value]
                break
            case FilterCategory.articleType:
                if (scope) {
                    // if the filter has both scopes, then we omit the scope filter,
                    // because anyway it won't filter anything
                    scope = undefined
                } else {
                    scope = filter.value
                }
                break
            case FilterCategory.assetPurpose:
            case FilterCategory.reservationPurpose:
            case FilterCategory.offerPurpose:
                purposes.push(filter.value)
                break
            case FilterCategory.productLine:
                lineIds.push(filter.value)
                break
            case FilterCategory.productFamily:
                familyIds.push(filter.value)
                break
            case FilterCategory.productModel:
                modelIds.push(filter.value)
                break
            case FilterCategory.yearOfConstruction:
            case FilterCategory.labelConstructionYear:
                yearOfConstructions.push(filter.value)
                break
            case FilterCategory.emissionLevel:
                emissionLevels.push(filter.value)
                break
            case FilterCategory.inventoryNumber:
            case FilterCategory.labelInventoryNumber:
                inventoryNumbers.push(filter.value)
                break
            case FilterCategory.serialNumber:
            case FilterCategory.labelSerialNumber:
                serialNumbers.push(filter.value)
                break
            case FilterCategory.serialNumberMachine:
            case FilterCategory.labelSerialNumberMachine:
                serialNumbersMachine.push(filter.value)
                break
            case FilterCategory.manufacturerCode:
            case FilterCategory.labelManufacturerCode:
                manufacturerCodes.push(filter.value)
                break
            case FilterCategory.financeAssetLmid:
                financeAssetLmids.push(filter.value)
                break
            case FilterCategory.financeSupplierOrderNumber:
                financeSupplierOrderNumber.push(filter.value)
                break
            case FilterCategory.financeInternalOrderNumber:
                financeInternalOrderNumber.push(filter.value)
                break
            case FilterCategory.valuationStatus:
                valuationStatuses.push(filter.value)
                break
            case FilterCategory.invoiceNumber:
                invoiceNumbers.push(filter.value)
                break
            case FilterCategory.referenceNumber:
                referenceNumbers.push(filter.value)
                break
            case FilterCategory.referenceText:
                referenceTexts.push(filter.value)
                break
            case FilterCategory.assignmentStatus:
                assignmentStatuses.push(filter.value)
                break
            case FilterCategory.customerName:
                customerNames.push(filter.value)
                break
            case FilterCategory.customerNumber:
                customerNumbers.push(filter.value)
                break
            case FilterCategory.crmReferenceNumber:
                crmReferenceNumbers.push(filter.value)
                break
            case FilterCategory.vatCode:
                vatCodes.push(filter.value)
                break
            case FilterCategory.drawingNumber:
            case FilterCategory.labelDrawingNumber:
                drawingNumbers.push(filter.value)
                break
            case FilterCategory.label:
                labeled.push(filter.value)
                break
            case FilterCategory.labelIdLinked:
            case FilterCategory.labelIdNotLinked:
                labels.push(filter.value)
                break
            case FilterCategory.operationalState:
                operational.push(filter.value)
                break
            case FilterCategory.connectedAssets:
                connectedAssets.push(filter.value)
                break
            case FilterCategory.shortage:
                shortages.push(filter.value)
                break
            case FilterCategory.missingDepreciationRate:
                missingDepreciationRates.push(filter.value)
                break
            case FilterCategory.ownershipType:
                if (ownershipType) {
                    // if the filter has both ownershipTypes, then we omit the ownershipType filter,
                    // because anyway it won't filter anything
                    ownershipType = undefined
                } else {
                    ownershipType = filter.value
                }
                break
            case FilterCategory.reservationStatus:
                reservationStatuses.push(filter.value)
                break
            case FilterCategory.salesAdmin:
                salesAdminNumbers.push(filter.value)
                break
            case FilterCategory.salesPerson:
                salesPersonNumbers.push(filter.value)
                break
            case FilterCategory.homebase:
                homebaseIds.push(filter.value)
                break
            case FilterCategory.warehouse:
            case FilterCategory.reservationWarehouse:
            case FilterCategory.fromToLocation:
                warehouseIds.push(filter.value)
                break
            case FilterCategory.responsibleWarehouse:
                responsibleWarehouseIds.push(filter.value)
                break
            case FilterCategory.profitCenter:
                profitCenterIds.push(filter.value)
                break
            case FilterCategory.color:
                colorIds.push(filter.value)
                break
            case FilterCategory.shippingOrderType:
                shippingOrderTypes.push(filter.value)
                break
            case FilterCategory.shippingOrderState:
                shippingOrderStates.push(filter.value)
                break
            case FilterCategory.shippingOrderNumber:
                shippingOrderNumbers.push(filter.value)
                break
            case FilterCategory.outgoingInvoiceType:
                outgoingInvoiceTypes.push(filter.value)
                break
            case FilterCategory.outgoingInvoiceStatus:
                outgoingInvoiceStatuses.push(filter.value)
                break
            case FilterCategory.erpOrderNumber:
                erpOrderNumbers.push(filter.value)
                break
            case FilterCategory.erpInvoiceNumber:
                erpInvoiceNumbers.push(filter.value)
                break
            case FilterCategory.stateOfStorage:
                if (stateOfStorage) {
                    // if the filter has both states, then we omit the state filter,
                    // because anyway it won't filter anything
                    stateOfStorage = undefined
                } else {
                    stateOfStorage = filter.value
                }
                break
            case FilterCategory.notificationChannel:
                notificationChannels.push(filter.value)
                break
            case FilterCategory.notificationType:
                notificationTypes.push(filter.value)
                break
            case FilterCategory.offerStatus:
                offerStatuses.push(filter.value)
                break
            case FilterCategory.offerNumber:
                offerNumbers.push(filter.value)
                break
            case FilterCategory.startsWithinDays:
                startsWithinDays = filter.value
                break
            case FilterCategory.endsWithinDays:
                endsWithinDays = filter.value
                break
            case FilterCategory.automaticInvoicing:
                automaticInvoicing.push(filter.value)
                break
            case FilterCategory.disposalDateDiffersCreationPeriod:
                disposalDateDiffersCreationPeriod = filter.value
                break
            case FilterCategory.disposalDateDiffersInvoicePeriod:
                disposalDateDiffersInvoicePeriod = filter.value
                break
            case FilterCategory.invoiceDateDiffersCreationPeriod:
                invoiceDateDiffersCreationPeriod = filter.value
                break
            case FilterCategory.invoicePendingMoreThanDays:
                invoicePendingMoreThanDays = filter.value
                break
            case FilterCategory.maxDistance:
                maxDistance = filter.value
                break
            case FilterCategory.articleClass:
                articleClasses.push(filter.value)
                break
            case FilterCategory.condition:
                condition = filter.value
                break
            case FilterCategory.initialCondition:
                initialCondition = filter.value
                break
            case FilterCategory.cuttingEdge:
                cuttingEdge = filter.value
                break
            case FilterCategory.teeth:
                teeth = filter.value
                break
            case FilterCategory.heavyDuty:
                heavyDuty = filter.value
                break
            case FilterCategory.availability:
                if (Array.isArray(filter.value)) {
                    availabilities.push(...filter.value)
                } else {
                    availabilities.push(filter.value)
                }
                break
            case FilterCategory.availabilityDate:
                availabilityDate = filter.value
                break
            case FilterCategory.lastScanDate:
                lastScanDate = filter.value
                break
            case FilterCategory.quickCoupler:
                quickCouplingSystems.push(filter.value)
                break
            case FilterCategory.connectionType:
                connectionTypes.push(filter.value)
                break
            case FilterCategory.locking:
                locking.push(filter.value)
                break
            case FilterCategory.controlSystem:
                controlSystems.push(filter.value)
                break
            case FilterCategory.agility:
                agility.push(filter.value)
                break
            case FilterCategory.referenceNumberSale:
                referenceNumbersSale.push(filter.value)
                break
            case FilterCategory.referenceNumberRental:
                referenceNumbersRental.push(filter.value)
                break
            case FilterCategory.assetType:
                assetTypes.push(filter.value)
                break
            case FilterCategory.length:
                length = filter.value
                break
            case FilterCategory.width:
                width = filter.value
                break
            case FilterCategory.height:
                height = filter.value
                break
            case FilterCategory.volume:
                volume = filter.value
                break
            case FilterCategory.costCategory:
                costCategoryIds.push(filter.value)
                break
            case FilterCategory.shippingOrderCompleteMode:
                completeModes.push(filter.value)
                break
            case FilterCategory.articleNumber:
            case FilterCategory.labelArticleNumber:
                articleNumbers.push(filter.value)
                break
            case FilterCategory.labelCategory:
                categoryIds.push(filter.value)
                break
            case FilterCategory.labelStatus:
                labelStatus.push(filter.value)
                break
            case FilterCategory.invoiceDate:
                invoiceDate = filter.value
                break
            case FilterCategory.role:
                roles.push(filter.value)
                break
            case FilterCategory.assetCategory:
                assetCategories.push(filter.value)
                break
            case FilterCategory.division:
                divisions.push(filter.value)
                break
            case FilterCategory.tenant:
                tenantIds.push(filter.value)
                break
            case FilterCategory.maintenanceType:
                maintenanceTypeIds.push(filter.value)
                break
            case FilterCategory.maintenanceStatus:
            case FilterCategory.assetLifeCycle:
                statuses.push(filter.value)
                break
            case FilterCategory.internalOrderNumber:
                internalOrderNumbers.push(filter.value)
                break
            case FilterCategory.supplierOrderNumber:
                supplierOrderNumbers.push(filter.value)
                break
            case FilterCategory.lastInvoice:
                lastInvoice = filter.value
                break
            case FilterCategory.invoiceBeforeShipping:
                invoiceBeforeShipping = filter.value
                break
            case FilterCategory.invoiceBlocked:
                invoiceBlocked = filter.value
                break
            case FilterCategory.rentToBuy:
                if (typeof filter.value === 'number') {
                    if (rentToBuy === undefined) rentToBuy = filter.value
                    else rentToBuy = undefined
                }
                break
        }
    }

    const result: { [x: string]: any } = {}

    if (criteria) {
        result.criteria = criteria
    }

    if (customerNameCriteria) {
        result.customerNameCriteria = customerNameCriteria
    }

    if (scope) {
        result.scope = scope
    }

    if (articleIds.length) {
        result.articleIds = articleIds
    }

    if (assetIds.length > 0) {
        result.assetIds = assetIds
    }

    if (purposes.length > 0) {
        result.purposes = purposes
    }

    if (lineIds.length > 0) {
        result.lineIds = lineIds
    }

    if (familyIds.length > 0) {
        result.familyIds = familyIds
    }

    if (modelIds.length > 0) {
        result.modelIds = modelIds
    }

    if (yearOfConstructions.length > 0) {
        result.yearOfConstructions = yearOfConstructions
    }

    if (emissionLevels.length > 0) {
        result.emissionLevels = emissionLevels
    }

    if (reservationNumbers.length > 0) {
        result.reservationNumbers = reservationNumbers
    }

    if (inventoryNumbers.length > 0) {
        result.inventoryNumbers = inventoryNumbers
    }

    if (serialNumbers.length > 0) {
        result.serialNumbers = serialNumbers
    }

    if (serialNumbersMachine.length > 0) {
        result.serialNumbersMachine = serialNumbersMachine
    }

    if (manufacturerCodes.length > 0) {
        result.manufacturerCodes = manufacturerCodes
    }

    if (financeAssetLmids.length > 0) {
        result.lmids = financeAssetLmids
    }

    if (financeSupplierOrderNumber.length > 0) {
        result.financeSupplierOrderNumber = financeSupplierOrderNumber
    }

    if (financeInternalOrderNumber.length > 0) {
        result.financeInternalOrderNumber = financeInternalOrderNumber
    }

    if (valuationStatuses.length > 0) {
        result.statuses = valuationStatuses
    }

    if (invoiceNumbers.length > 0) {
        result.invoiceNumbers = invoiceNumbers
    }

    if (referenceNumbers.length > 0) {
        result.referenceNumbers = referenceNumbers
    }

    if (referenceTexts.length > 0) {
        result.referenceTexts = referenceTexts
    }

    if (assignmentStatuses.length > 0) {
        result.assignmentStatuses = assignmentStatuses
    }

    if (customerNames.length > 0) {
        result.customerNames = customerNames
    }

    if (customerNumbers.length > 0) {
        result.customerNumbers = customerNumbers
    }

    if (crmReferenceNumbers.length > 0) {
        result.crmReferenceNumbers = crmReferenceNumbers
    }

    if (vatCodes.length > 0) {
        result.vatCodes = vatCodes
    }

    if (drawingNumbers.length > 0) {
        result.drawingNumbers = drawingNumbers
    }

    if (labeled.length === 1) {
        result.isLabeled = labeled[0]
    }

    if (labels.length > 0) {
        result.labels = labels
    }

    if (operational.length === 1) {
        result.isOperational = operational[0]
    }

    if (connectedAssets.length === 1) {
        result.connectedAssets = connectedAssets[0]
    }

    if (ownershipType) {
        result.ownershipType = ownershipType
    }

    if (assetStatus.length === 1) {
        result.status = assetStatus[0]
    }

    if (reservationStatuses.length > 0) {
        result.statuses = reservationStatuses
    }

    if (salesAdminNumbers.length > 0) {
        result.salesAdminNumbers = salesAdminNumbers
    }

    if (salesPersonNumbers.length > 0) {
        result.salesPersonNumbers = salesPersonNumbers
    }

    if (homebaseIds.length > 0) {
        result.homebaseIds = homebaseIds
    }

    if (warehouseIds.length > 0) {
        result.warehouseIds = warehouseIds
    }

    if (responsibleWarehouseIds.length > 0) {
        result.responsibleWarehouseIds = responsibleWarehouseIds
    }

    if (profitCenterIds.length > 0) {
        result.profitCenterIds = profitCenterIds
    }

    if (colorIds.length > 0) {
        result.colorIds = colorIds
    }

    if (shippingOrderTypes.length > 0) {
        result.types = shippingOrderTypes
    }

    if (shippingOrderStates.length > 0) {
        result.statuses = shippingOrderStates
    }

    if (shippingOrderNumbers.length > 0) {
        result.orderNumbers = shippingOrderNumbers
    }

    if (stateOfStorage) {
        result.stateOfStorage = stateOfStorage
    }

    if (outgoingInvoiceTypes.length > 0) {
        result.invoiceTypes = outgoingInvoiceTypes
    }

    if (outgoingInvoiceStatuses.length > 0) {
        result.invoiceStatuses = outgoingInvoiceStatuses
    }

    if (erpOrderNumbers.length > 0) {
        result.erpOrderNumbers = erpOrderNumbers
    }

    if (erpInvoiceNumbers.length > 0) {
        result.erpInvoiceNumbers = erpInvoiceNumbers
    }

    if (notificationChannels.length > 0) {
        result.notificationDomains = notificationChannels
    }

    if (notificationTypes.length > 0) {
        result.notificationTypes = notificationTypes
    }

    if (offerStatuses.length > 0) {
        result.statuses = offerStatuses
    }

    if (offerNumbers.length > 0) {
        result.offerNumbers = offerNumbers
    }

    if (shortages.length === 1) {
        result.shortages = shortages[0]
    }
    if (missingDepreciationRates.length === 1) {
        result.missingDepreciationRate = missingDepreciationRates[0]
    }

    if (typeof startsWithinDays === "number") {
        result.startsWithinDays = startsWithinDays
    }

    if (typeof endsWithinDays === "number") {
        result.endsWithinDays = endsWithinDays
    }

    if (automaticInvoicing.length === 1) {
        result.automaticInvoicing = automaticInvoicing[0]
    }

    if (typeof disposalDateDiffersCreationPeriod === 'boolean') {
        result.disposalDateDiffersCreationPeriod = disposalDateDiffersCreationPeriod
    }

    if (typeof disposalDateDiffersInvoicePeriod === 'boolean') {
        result.disposalDateDiffersInvoicePeriod = disposalDateDiffersInvoicePeriod
    }

    if (typeof invoiceDateDiffersCreationPeriod === 'boolean') {
        result.invoiceDateDiffersCreationPeriod = invoiceDateDiffersCreationPeriod
    }

    if (typeof invoicePendingMoreThanDays === 'number') {
        result.invoicePendingMoreThanDays = invoicePendingMoreThanDays
    }

    if (typeof maxDistance === 'number') {
        result.maxDistance = maxDistance * 1000 // convert to meters
    }

    if (condition) {
        if (typeof condition[0] === "number") {
            result.conditionFrom = condition[0]
        }
        if (typeof condition[1] === "number") {
            result.conditionTo = condition[1]
        }
    }

    if (typeof initialCondition === 'boolean') {
        result.initialCondition = initialCondition
    }

    if (typeof cuttingEdge === 'boolean') {
        result.cuttingEdge = cuttingEdge
    }

    if (typeof teeth === 'boolean') {
        result.teeth = teeth
    }

    if (typeof heavyDuty === 'boolean') {
        result.heavyDuty = heavyDuty
    }

    if (articleClasses.length > 0) {
        result.articleClasses = articleClasses
    }

    if (availabilities.length > 0) {
        result.availabilities = availabilities
    }

    if (availabilityDate) {
        if (availabilityDate[0]) {
            result.availabilityDateFrom = availabilityDate[0]
        }
        if (availabilityDate[1]) {
            result.availabilityDateTo = availabilityDate[1]
        }
    }

    if (lastScanDate) {
        if (lastScanDate[0]) {
            result.lastScanDateFrom = lastScanDate[0]
        }
        if (lastScanDate[1]) {
            result.lastScanDateTo = lastScanDate[1]
        }
    }

    if (quickCouplingSystems.length > 0) {
        result.quickCouplingSystems = quickCouplingSystems
    }

    if (connectionTypes.length > 0) {
        result.connectionTypes = connectionTypes
    }

    if (locking.length > 0) {
        result.locking = locking
    }

    if (controlSystems.length > 0) {
        result.controlSystems = controlSystems
    }

    if (agility.length > 0) {
        result.agility = agility
    }

    if (referenceNumbersSale.length > 0) {
        result.referenceNumbersSale = referenceNumbersSale
    }

    if (referenceNumbersRental.length > 0) {
        result.referenceNumbersRental = referenceNumbersRental
    }

    if (assetTypes.length > 0) {
        result.assetTypes = assetTypes
    }

    if (length) {
        if (typeof length[0] === "number") {
            result.lengthFrom = length[0]
        }
        if (typeof length[1] === "number") {
            result.lengthTo = length[1]
        }
    }

    if (width) {
        if (typeof width[0] === "number") {
            result.widthFrom = width[0]
        }
        if (typeof width[1] === "number") {
            result.widthTo = width[1]
        }
    }

    if (height) {
        if (typeof height[0] === "number") {
            result.heightFrom = height[0]
        }
        if (typeof height[1] === "number") {
            result.heightTo = height[1]
        }
    }

    if (volume) {
        if (typeof volume[0] === "number") {
            result.volumeFrom = volume[0]
        }
        if (typeof volume[1] === "number") {
            result.volumeTo = volume[1]
        }
    }

    if (costCategoryIds.length > 0) {
        result.costCategoryIds = costCategoryIds
    }

    if (completeModes.length > 0) {
        result.completeModes = completeModes
    }

    if (articleNumbers.length > 0) {
        result.articleNumbers = articleNumbers
    }

    if (categoryIds.length > 0) {
        result.categoryIds = categoryIds
    }

    if (labelStatus.length === 1) {
        result.labelStatuses = [labelStatus[0]]
    }

    if (invoiceDate) {
        result.invoiceDate = invoiceDate
    }

    if (roles.length > 0) {
        result.roles = roles
    }

    if (assetCategories.length > 0) {
        result.assetCategories = assetCategories
    }

    if (divisions.length > 0) {
        result.divisions = divisions
    }

    if (tenantIds.length > 0) {
        result.tenantIds = tenantIds
    }

    if (maintenanceTypeIds.length > 0) {
        result.maintenanceTypeIds = maintenanceTypeIds
    }

    if (statuses.length > 0) {
        result.statuses = statuses
    }

    if (internalOrderNumbers.length > 0) {
        result.internalOrderNumbers = internalOrderNumbers
    }

    if (supplierOrderNumbers.length > 0) {
        result.supplierOrderNumbers = supplierOrderNumbers
    }

    if (typeof lastInvoice === 'boolean') {
        result.lastInvoice = lastInvoice
    }

    if (typeof invoiceBeforeShipping === 'boolean') {
        result.invoiceBeforeShipping = invoiceBeforeShipping
    }

    if (typeof invoiceBlocked === 'boolean') {
        result.invoiceBlocked = invoiceBlocked
    }

    if (typeof rentToBuy === 'number') {
        result.rentToBuy = rentToBuy
    }

    return result
}
